import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import type { Indicator } from '@/types/survey';

interface UseIndicatorConflictsProps {
  indicators: Indicator[];
  selectedIndicators: string[];
}

interface ConflictInfo {
  hasConflict: boolean;
  conflictingIndicator?: Indicator;
  domainId?: string;
}

export const useIndicatorConflicts = ({ indicators, selectedIndicators }: UseIndicatorConflictsProps) => {
  
  // Check if an indicator conflicts with already selected ones
  // Conflict is based on domainId: two indicators with the same domainId cannot be selected
  const checkConflict = useCallback((indicator: Indicator): ConflictInfo => {
    const domainId = indicator.domainId || 'unknown';
    if (!domainId || domainId === 'unknown') {
      return { hasConflict: false };
    }

    const alreadySelectedIndicators = indicators.filter(ind => 
      selectedIndicators.includes(ind.indicatorId) && ind.indicatorId !== indicator.indicatorId
    );

    const conflictingIndicator = alreadySelectedIndicators.find(selectedInd => 
      (selectedInd.domainId || 'unknown') === domainId
    );

    return {
      hasConflict: !!conflictingIndicator,
      conflictingIndicator,
      domainId: domainId
    };
  }, [indicators, selectedIndicators]);

  // Check if adding an indicator would create a conflict
  const wouldCreateConflict = useCallback((indicatorId: string): boolean => {
    const indicator = indicators.find(ind => ind.indicatorId === indicatorId);
    if (!indicator) return false;
    
    const conflict = checkConflict(indicator);
    return conflict.hasConflict;
  }, [indicators, checkConflict]);

  // Handle bulk selection with conflict resolution
  // Prioritizes non-B2C variants and "with Self-Harm" variants
  const resolveBulkSelection = useCallback((
    indicatorsToAdd: Indicator[],
    onConflict?: (skipped: string[]) => void
  ): string[] => {
    const alreadySelectedIndicators = indicators.filter(ind => 
      selectedIndicators.includes(ind.indicatorId)
    );

    // Group indicators by domainId to handle conflicts
    const domainGroups: Record<string, Indicator[]> = {};
    for (const indicator of indicatorsToAdd) {
      const domainId = indicator.domainId || 'unknown';
      if (!domainGroups[domainId]) {
        domainGroups[domainId] = [];
      }
      domainGroups[domainId].push(indicator);
    }

    const validIndicatorsToAdd: string[] = [];
    const skippedConflicts: string[] = [];

    // Process each domain group
    for (const [domainId, domainIndicators] of Object.entries(domainGroups)) {
      // Check if this domainId is already selected
      const conflictingIndicator = alreadySelectedIndicators.find(selectedInd => 
        (selectedInd.domainId || 'unknown') === domainId
      );

      if (conflictingIndicator) {
        // Skip all indicators in this domain due to conflict
        skippedConflicts.push(domainId);
        continue;
      }

      // Prioritize: non-B2C first, then "with Self-Harm" variant
      const sortedIndicators = domainIndicators.sort((a, b) => {
        const aVariant = a.variant?.toLowerCase() || '';
        const bVariant = b.variant?.toLowerCase() || '';
        
        const aIsB2C = aVariant === 'b2c';
        const bIsB2C = bVariant === 'b2c';
        
        // Non-B2C comes before B2C
        if (aIsB2C !== bIsB2C) {
          return aIsB2C ? 1 : -1;
        }
        
        // If both are B2C or both are non-B2C, prioritize "with Self-Harm"
        const aIsWithSelfHarm = aVariant.includes('with self-harm') || 
                                aVariant.includes('with-self-harm') ||
                                aVariant.includes('withselfharm');
        const bIsWithSelfHarm = bVariant.includes('with self-harm') || 
                                bVariant.includes('with-self-harm') ||
                                bVariant.includes('withselfharm');
        
        if (aIsWithSelfHarm !== bIsWithSelfHarm) {
          return aIsWithSelfHarm ? -1 : 1;
        }
        
        return 0;
      });

      // Add the first (highest priority) indicator from this domain
      if (sortedIndicators.length > 0) {
        validIndicatorsToAdd.push(sortedIndicators[0].indicatorId);
      }
    }

    // Notify about conflicts
    if (skippedConflicts.length > 0) {
      const uniqueConflicts = [...new Set(skippedConflicts)];
      onConflict?.(uniqueConflicts);
    }

    return validIndicatorsToAdd;
  }, [indicators, selectedIndicators]);

  // Validate single indicator selection
  const validateSelection = useCallback((indicatorId: string): boolean => {
    const indicator = indicators.find(ind => ind.indicatorId === indicatorId);
    if (!indicator) return true;

    const conflict = checkConflict(indicator);
    if (conflict.hasConflict) {
      const domainId = indicator.domainId || 'unknown';
      toast.error(`Cannot select this indicator - an indicator with domain "${domainId}" is already selected.`);
      return false;
    }

    return true;
  }, [indicators, checkConflict]);

  // Get all conflicted indicators (indicators that can't be selected)
  const conflictedIndicators = useMemo(() => {
    return indicators.filter(indicator => {
      if (selectedIndicators.includes(indicator.indicatorId)) {
        return false; // Already selected indicators are not conflicted
      }
      const conflict = checkConflict(indicator);
      return conflict.hasConflict;
    });
  }, [indicators, selectedIndicators, checkConflict]);

  // Check if an indicator has duplicates within its type group (by domainId)
  const hasDuplicateDomainId = useCallback((indicator: Indicator, typeIndicators: Indicator[]): boolean => {
    const domainId = indicator.domainId || 'unknown';
    if (!domainId || domainId === 'unknown') return false;
    
    const sameDomainIdCount = typeIndicators.filter(
      other => (other.domainId || 'unknown') === domainId
    ).length;
    
    return sameDomainIdCount > 1;
  }, []);

  return {
    checkConflict,
    wouldCreateConflict,
    validateSelection,
    resolveBulkSelection,
    conflictedIndicators,
    hasDuplicateDomainId
  };
};